import './Chart.css';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTheme } from '@mui/material';

/**
 * The formula for the hill curve used in the original library (hill-chart).
 * It uses a sine wave to create the characteristic hill shape from x=0 to x=100.
 * 
 * @param {number} t - The x-coordinate on the hill (0 to 100).
 * @returns {number} The corresponding y-coordinate on the hill (0 to 100).
 */
const Te = (t) => 50 * Math.sin((Math.PI / 50) * t - 0.5 * Math.PI) + 50;

/**
 * Simple AABB (Axis-Aligned Bounding Box) collision detection.
 * Used to check if two label boxes overlap.
 * 
 * @param {Object} box1 - First bounding box with xMin, xMax, yMin, yMax.
 * @param {Object} box2 - Second bounding box with xMin, xMax, yMin, yMax.
 * @returns {boolean} True if the boxes overlap, false otherwise.
 */
const overlaps = (box1, box2) => {
  return (
    box1.xMin < box2.xMax &&
    box1.xMax > box2.xMin &&
    box1.yMin < box2.yMax &&
    box1.yMax > box2.yMin
  );
};

/**
 * A group is rendered as a single numbered bubble with a hover fan. Scopes parked at
 * 0% or 100% are excluded: they stack vertically instead of grouping.
 *
 * @param {Array<Object>} group - A group of snapshots sharing one anchor.
 * @returns {boolean} True if the group renders as a bubble rather than a plain dot.
 */
const isGrouped = (group) => group.length > 1 && group[0].x > 0 && group[0].x < 100;

/**
 * HillChartCustom component for visualizing project progress on a hill curve.
 * Supports grouping of overlapping dots and fanned-out labels on hover.
 * 
 * @param {Object} props - Component properties.
 * @param {Array<Object>} props.snapshots - Array of data points to display on the chart.
 * @param {number} [props.width=962] - Width of the SVG container.
 * @param {number} [props.height=300] - Height of the SVG container.
 * @param {Object} [props.margin] - Margins around the chart area.
 * @param {Function} [props.onMoved] - Callback triggered when a dot is moved.
 * @param {boolean} [props.preview=false] - If true, disables interaction.
 * @param {boolean} [props.darkMode=false] - If true, applies dark mode colors.
 * @param {boolean} [props.showGroups=true] - If true, groups overlapping dots.
 * @returns {JSX.Element} The rendered hill chart component.
 */
const HillChartCustom = (props) => {
  const {
    snapshots: initialSnapshots,
    width = 962,
    height = 300,
    margin = { top: 15, right: 10, bottom: 35, left: 10 },
    onMoved,
    preview = false,
    darkMode = false,
    showGroups = true
  } = props;

  const theme = useTheme();
  const [snapshots, setSnapshots] = useState([]);
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [dragging, setDragging] = useState(null); // { id, type: 'dot' | 'title' }
  const [dragOffset, setDragOffset] = useState(0);
  const svgRef = useRef(null);

  // Constraints for the hill chart size to ensure it fits within the SVG container.
  const maxChartWidth = 900;
  const maxChartHeight = 250;

  const chartWidth = Math.min(width - 20, maxChartWidth);
  const chartHeight = Math.min(height - margin.top - margin.bottom, maxChartHeight);

  // Centering the chart within the available SVG space.
  const marginLeft = (width - chartWidth) / 2;
  const marginTop = (height - chartHeight) / 2;

  useEffect(() => {
    // Normalizes input snapshots and handles vertical stacking for items at 0% or 100%.
    let stackedY0 = 0;
    let stackedY100 = 0;

    const data = initialSnapshots.map(s => {
      const x = s.hill_chart_x !== undefined ? s.hill_chart_x : (s.x !== undefined ? s.x : 0);
      let y = Te(x);

      // Vertical stacking logic: if multiple items are at the start or end, 
      // they stack upwards instead of overlapping on the curve.
      if (x === 0) {
        y = stackedY0;
        stackedY0 += 8;
      } else if (x === 100) {
        y = stackedY100;
        stackedY100 += 8;
      }

      const id = s.scope_id || s.id;

      return {
        ...s,
        x,
        y,
        id: id || `temp-${s.date || 'no-date'}-${Math.random().toString(36).slice(-6)}`,
        description: s.description || s.title || '',
        color: s.color || '#3498db',
        size: s.size || 10
      };
    });
    setSnapshots(data);
  }, [initialSnapshots]);

  // Scaling functions to convert 0-100 coordinates to pixel values.
  const xScale = useCallback((x) => (x / 100) * chartWidth, [chartWidth]);
  const yScale = useCallback((y) => chartHeight - (y / 100) * chartHeight, [chartHeight]);
  const invXScale = useCallback((pixelX) => (pixelX / chartWidth) * 100, [chartWidth]);

  const colorScheme = darkMode ? "hill-chart-dark" : "hill-chart-light";

  /**
   * Memoized calculation of dot groups.
   * Dots are clustered into a single group if they are physically touching or overlapping.
   * Chained grouping is prevented by always comparing against the group's anchor dot (first dot).
   * 
   * @type {Array<Array<Object>>} An array of groups, where each group is an array of snapshots.
   */
  const groups = useMemo(() => {
    if (!showGroups) return snapshots.map(s => [s]);

    const getX = (val) => (val / 100) * chartWidth;
    const getY = (val) => chartHeight - (Te(val) / 100) * chartHeight;
    const isTouching = (a, b) => {
      const dx = getX(a.x) - getX(b.x);
      const dy = getY(a.x) - getY(b.x);
      const distance = Math.sqrt(dx * dx + dy * dy);
      // Touch check: distance <= sum of radii.
      return distance <= (a.size + b.size);
    };

    const dragId = dragging?.id;
    // We exclude the dragged item from grouping logic to allow it to "pass through" other dots.
    const snapshotsToGroup = snapshots.filter(s => s.id !== dragId && s.x > 0 && s.x < 100);
    const sorted = [...snapshotsToGroup].sort((a, b) => a.x - b.x);
    const result = [];

    if (sorted.length > 0) {
      let currentGroup = [sorted[0]];
      for (let i = 1; i < sorted.length; i++) {
        // Compare with the first dot in the group (the visible anchor)
        // to prevent the grouping area from expanding "invisibly"
        if (isTouching(sorted[i], currentGroup[0])) {
          currentGroup.push(sorted[i]);
        } else {
          result.push(currentGroup);
          currentGroup = [sorted[i]];
        }
      }
      result.push(currentGroup);
    }

    // Items at 0 and 100 are never grouped; they stack vertically. The dragged item
    // is excluded here too: dragging clamps to 0..100, so landing exactly on either
    // end would push it once here and again below, duplicating its React key.
    snapshots
      .filter(s => s.id !== dragId && (s.x === 0 || s.x === 100))
      .forEach(s => result.push([s]));

    if (dragging) {
      const draggedItem = snapshots.find(s => s.id === dragId);
      if (draggedItem) result.push([draggedItem]);
    }

    return result;
  }, [snapshots, dragging, showGroups, chartWidth, chartHeight]);

  /**
   * Memoized map of fanned-out label positions for each group.
   * Calculates optimal angles and radii for labels to avoid overlapping with:
   * 1. Other labels within the same group (internal collision).
   * 2. Static dots and their labels on the chart (external collision).
   * 3. The boundaries of the SVG container.
   * 
   * @type {Object.<string, Array<Object>>} A map where keys are group anchor IDs and values are label data.
   */
  const fannedLabelsMap = useMemo(() => {
    const map = {};
    groups.forEach(group => {
      const isGroup = isGrouped(group);
      if (!isGroup) return;

      const mainDot = group[0];
      const fannedLabels = [];
      const total = group.length;
      const { x, y } = mainDot;
      const dotPx = xScale(mainDot.x);
      const dotPy = yScale(mainDot.y);
      let centerAngle;

      /**
       * Quadrant-based fanning logic:
       * - Bottom-Left: Fan Top-Right
       * - Bottom-Right: Fan Top-Left
       * - Top half: Fan Downwards (to avoid UI elements above the chart)
       */
      if (y < 50) {
        centerAngle = x < 50 ? -Math.PI / 4 : -3 * Math.PI / 4;
      } else {
        centerAngle = Math.PI / 2;
      }

      const angleRange = y < 50 ? Math.PI * 0.6 : Math.PI;
      const startAngle = centerAngle - angleRange / 2;

      // Identify boxes of other static dots/labels to avoid overlapping them.
      const staticBoxes = [];
      groups.forEach(g => {
        if (g[0].id === mainDot.id) return;
        const d = g[0];
        // Scopes parked at 0% / 100% are faded and painted below the fan, so labels
        // may sit over them. Avoiding them would push the fan of a group near either
        // end out to its maximum radius, where labels start overlapping each other.
        if (d.x === 0 || d.x === 100) return;
        const isG = isGrouped(g);
        const dPx = xScale(d.x);
        const dPy = yScale(d.y);
        const r = isG ? 12 : d.size;

        staticBoxes.push({
          xMin: dPx - r - 2, xMax: dPx + r + 2,
          yMin: dPy - r - 2, yMax: dPy + r + 2
        });

        if (!isG) {
          const lWidth = (d.description || '').length * 8;
          const isEndDot = d.x >= 80;
          const lTx = isEndDot ? -1 * (d.size + 5) : (d.size + 5);
          staticBoxes.push({
            xMin: dPx + (isEndDot ? lTx - lWidth : lTx),
            xMax: dPx + (isEndDot ? lTx : lTx + lWidth),
            yMin: dPy + 5 - 10, yMax: dPy + 5 + 10
          });
        }
      });

      // Position each label in the group arc, pushing outward if a collision is detected.
      group.forEach((dot, dIdx) => {
        const angle = total > 1 ? startAngle + (dIdx / (total - 1)) * angleRange : centerAngle;
        let radius = (total > 5 && dIdx % 2 !== 0) ? 75 : 55;
        let tx, ty, box;
        let collision = true;
        let attempts = 0;
        const MAX_RADIUS = 110;
        const charWidth = 8;
        const labelHeight = 20;

        while (collision && attempts < 10) {
          tx = Math.cos(angle) * radius;
          ty = Math.sin(angle) * radius;
          const isStart = tx >= 0;
          const labelWidth = (dot.description || '').length * charWidth;

          const currentBox = {
            xMin: isStart ? tx + 5 : tx - 5 - labelWidth,
            xMax: isStart ? tx + 5 + labelWidth : tx - 5,
            yMin: ty - labelHeight / 2,
            yMax: ty + labelHeight / 2,
            labelWidth,
            isStart
          };

          const globalBox = {
            xMin: dotPx + currentBox.xMin, xMax: dotPx + currentBox.xMax,
            yMin: dotPy + currentBox.yMin, yMax: dotPy + currentBox.yMax
          };

          collision = fannedLabels.some(other => overlaps(currentBox, other.box)) ||
            staticBoxes.some(other => overlaps(globalBox, other)) ||
            (globalBox.xMin < -marginLeft || globalBox.xMax > width - marginLeft ||
              globalBox.yMin < -marginTop || globalBox.yMax > height - marginTop);

          if (collision && radius < MAX_RADIUS) {
            radius = Math.min(radius + 10, MAX_RADIUS);
            attempts++;
          } else {
            box = currentBox; // Save the valid box
            collision = false;
          }
        } fannedLabels.push({ dot, tx, ty, box });
      });
      map[mainDot.id] = fannedLabels;
    });
    return map;
  }, [groups, width, height, marginLeft, marginTop, xScale, yScale]);

  /**
   * Paint order drives both stacking and hit-testing in SVG, so it doubles as the
   * interaction priority. Layers, from bottom-most to top-most:
   *   0 - scopes parked at 0% / 100%, rendered faded and never raised on hover
   *   1 - single scopes on the curve            2 - the hovered single scope
   *   3 - grouped scopes                        4 - the hovered group
   *   5 - the scope being dragged
   * Groups outrank single scopes so their bubble and hover fan stay reachable even
   * when a faded scope sits next to them at either end of the hill. This is also
   * what keeps an open fan open: the hovered group covers the scopes underneath with
   * its own hit areas, so the pointer never crosses onto them. Leaving those areas
   * closes the fan regardless of what lies beneath.
   *
   * @type {Array<Array<Object>>} The groups ordered from bottom-most to top-most.
   */
  const sortedGroups = useMemo(() => {
    const layerOf = (group) => {
      const dot = group[0];
      if (dragging && dot.id === dragging.id) return 5;
      if (dot.x === 0 || dot.x === 100) return 0;
      if (dot.id === hoveredGroup) return isGrouped(group) ? 4 : 2;
      return isGrouped(group) ? 3 : 1;
    };

    return [...groups]
      .map((group, index) => ({ group, index, layer: layerOf(group) }))
      .sort((a, b) => a.layer - b.layer || a.index - b.index)
      .map(entry => entry.group);
  }, [groups, hoveredGroup, dragging]);

  /**
   * Clears the hover only if the leaving group still owns it. React dispatches leave
   * before enter, so a plain transition never needs this, but changing the hover
   * reorders the groups and a group moved out from under the pointer can leave late.
   *
   * @param {string} groupId - The anchor id of the group the pointer left.
   */
  const handleGroupMouseLeave = (groupId) => {
    setHoveredGroup(prev => (prev === groupId ? null : prev));
  };

  const handleMouseDown = (e, id, type) => {
    if (preview) return;
    e.stopPropagation();

    const snapshot = snapshots.find(s => s.id === id);
    if (!snapshot) return;

    const CTM = svgRef.current.getScreenCTM();
    const mouseX = (e.clientX - CTM.e) / CTM.a - marginLeft;
    const currentPixelX = xScale(snapshot.x);

    setDragOffset(mouseX - currentPixelX);
    setDragging({ id, type });

    // Move the dragged item to the end of the array so it counts as the most
    // recently added one, which is what gives a group its color further below.
    // Stacking is handled by the layers in sortedGroups, not by this order.
    setSnapshots(prev => {
      const index = prev.findIndex(s => s.id === id);
      if (index === -1) return prev;
      const item = prev[index];
      const rest = prev.filter(s => s.id !== id);
      return [...rest, item];
    });
  };

  const handleMouseMove = (e) => {
    if (!dragging || !svgRef.current) return;

    const CTM = svgRef.current.getScreenCTM();
    const mouseX = (e.clientX - CTM.e) / CTM.a - marginLeft;

    let targetPixelX = mouseX - dragOffset;
    let newX = invXScale(targetPixelX);

    if (newX < 0) newX = 0;
    if (newX > 100) newX = 100;

    setSnapshots(prev => prev.map(s => {
      if (s.id === dragging.id) {
        return { ...s, x: newX, y: Te(newX) };
      }
      return s;
    }));
  };

  const handleMouseUp = () => {
    if (dragging) {
      const draggedItem = snapshots.find(s => s.id === dragging.id);
      if (draggedItem && onMoved) {
        onMoved({
          id: draggedItem.id,
          x: draggedItem.x,
          y: draggedItem.y
        });
      }
    }
    setDragging(null);
  };

  const hillPath = useMemo(() => {
    let path = `M ${xScale(0)} ${yScale(Te(0))}`;
    for (let i = 1; i <= 200; i++) {
      const x = i / 2;
      path += ` L ${xScale(x)} ${yScale(Te(x))}`;
    }
    return path;
  }, [xScale, yScale]);

  const isEnd = (x) => x >= 80;
  const getLabelX = (size, x) => isEnd(x) ? -1 * (size + 5) : (size + 5);
  const getLabelAnchor = (x) => isEnd(x) ? "end" : "start";

  return (
    <div className={`hill-chart-container ${colorScheme}`}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        className="hill-chart-custom"
        style={{ backgroundColor: 'transparent' }}
      >
        <g transform={`translate(${marginLeft}, ${marginTop})`}>
          <g className="hill-chart-bottom-line" transform={`translate(0, ${chartHeight + 5})`}>
            <path d={`M 0.5 0 L ${chartWidth} 0`} stroke="currentColor" fill="none" />
          </g>

          <path d={hillPath} className="chart-hill-main-curve" />

          <line
            x1={xScale(50)} y1={yScale(0)}
            x2={xScale(50)} y2={yScale(100)}
            className="hill-chart-middle-line"
          />

          <text key="figuring" x={xScale(25)} y={chartHeight + 30} className="hill-chart-text" style={{ fontSize: '0.75rem' }}>Figuring things out</text>
          <text key="making" x={xScale(75)} y={chartHeight + 30} className="hill-chart-text" style={{ fontSize: '0.75rem' }}>Making it happen</text>

          {sortedGroups.map((group) => {
            const isGroup = isGrouped(group);
            const mainDot = group[0];
            const isHovered = hoveredGroup === mainDot.id;
            const isStacked = mainDot.x === 0 || mainDot.x === 100;

            const lastAddedDot = isGroup ? [...group].sort((a, b) => {
              return snapshots.indexOf(b) - snapshots.indexOf(a);
            })[0] : mainDot;

            const groupColor = isGroup ? lastAddedDot.color : mainDot.color;
            const textColor = theme.palette.getContrastText(groupColor);

            return (
              <g key={mainDot.id}
                transform={`translate(${xScale(mainDot.x)},${yScale(mainDot.y)})`}
                onMouseEnter={() => setHoveredGroup(mainDot.id)}
                onMouseLeave={() => handleGroupMouseLeave(mainDot.id)}
                data-stacked={isStacked ? "true" : undefined}
              >
                {/* 
                   Invisible hover area to prevent the fanned-out labels 
                   from flickering when the mouse moves between lines. 
                */}
                {isGroup && isHovered && (
                  <circle r={60} fill="transparent" />
                )}

                {isGroup && isHovered && fannedLabelsMap[mainDot.id]?.map(({ dot, tx, ty, box }) => (
                  <g
                    key={dot.id}
                    className="hill-chart-group"
                    style={{ cursor: preview ? 'default' : 'grab' }}
                    onMouseDown={(e) => handleMouseDown(e, dot.id, 'title')}
                  >
                    {/* Invisible hit areas to make lines and labels easy to click. */}
                    <line x1={0} y1={0} x2={tx} y2={ty} stroke="transparent" strokeWidth="16" />
                    <rect
                      x={box.isStart ? tx : tx - box.labelWidth - 5}
                      y={ty - 12}
                      width={box.labelWidth + 10}
                      height={24}
                      fill="transparent"
                    />
                    <line x1={0} y1={0} x2={tx} y2={ty} stroke={dot.color} strokeWidth="2" opacity={0.6} />
                    <text
                      x={tx + (tx >= 0 ? 5 : -5)}
                      y={ty + (ty >= 0 ? 4 : -4)}
                      style={{ textAnchor: tx >= 0 ? 'start' : 'end', alignmentBaseline: 'middle' }}
                    >
                      {dot.description}
                    </text>
                  </g>
                ))}

                {!isGroup ? (
                  <g
                    className="hill-chart-group"
                    onMouseDown={(e) => handleMouseDown(e, mainDot.id, 'dot')}
                    style={{ cursor: preview ? 'default' : 'grab' }}
                  >
                    <circle r={mainDot.size} fill={mainDot.color} className="hill-chart-circle" />
                    <text
                      x={getLabelX(mainDot.size, mainDot.x)}
                      y="5"
                      style={{ textAnchor: getLabelAnchor(mainDot.x) }}
                    >
                      {mainDot.description}
                    </text>
                  </g>
                ) : (
                  <g>
                    <circle r={12} fill={groupColor} className="hill-chart-circle" style={{ cursor: 'pointer' }} />
                    <text y="5" textAnchor="middle" fontSize="12" fill={textColor} style={{ pointerEvents: 'none', fontWeight: 'bold' }}>{group.length}</text>
                  </g>
                )}
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
};

export default HillChartCustom;
